import React, { useEffect, useRef, useState, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';
import { useInput } from 'react-admin';
import { Box, TextField, Typography, Paper } from '@mui/material';
import { isPointInPolygon } from 'geolib';

// Исправляем проблему с иконками маркеров в Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface PolygonPoint {
  lat: number;
  lng: number;
}

interface PolygonMapInputProps {
  source?: string;
  polygonSource: string;
  colorSource?: string;
  defaultLat?: number;
  defaultLng?: number;
}

export const PolygonMapInput: React.FC<PolygonMapInputProps> = ({
  polygonSource,
  colorSource = 'color',
  defaultLat = 55.0084,
  defaultLng = 82.9357,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const drawnItemsRef = useRef<L.FeatureGroup | null>(null);
  const drawControlRef = useRef<L.Control.Draw | null>(null);
  const currentLayerRef = useRef<L.TileLayer | null>(null);

  const polygonInput = useInput({ source: polygonSource });
  const colorInput = useInput({ source: colorSource });

  const [localPolygon, setLocalPolygon] = useState<PolygonPoint[]>([]);
  const [localColor, setLocalColor] = useState<string>('#3388ff');
  const [isInitialized, setIsInitialized] = useState(false);
  const [currentRecordId, setCurrentRecordId] = useState<any>(null);
  const [mapType, setMapType] = useState<'map' | 'satellite'>('map');

  // Тестирование адреса
  const [testAddress, setTestAddress] = useState('');
  const [testResult, setTestResult] = useState<string>('');
  const testMarkersRef = useRef<L.Marker[]>([]);

  // Обновляем полигон в форме
  const updatePolygonInForm = useCallback(
    (points: PolygonPoint[] | null) => {
      setLocalPolygon(points || []);
      // Отправляем null если полигон пустой — чтобы @IsOptional() в DTO пропустил валидацию
      polygonInput.field.onChange(points && points.length > 0 ? points : null);
    },
    [polygonInput.field],
  );

  // Извлечь точки из слоя Leaflet
  const extractPolygonPoints = useCallback((layer: L.Polygon): PolygonPoint[] => {
    const latlngs = layer.getLatLngs()[0] as L.LatLng[];
    return latlngs.map((ll) => ({
      lat: Math.round(ll.lat * 1000000) / 1000000,
      lng: Math.round(ll.lng * 1000000) / 1000000,
    }));
  }, []);

  // Проверка попадания точки в полигон
  const isInPolygon = useCallback(
    (lat: number, lng: number, polygon: PolygonPoint[]): boolean => {
      if (!polygon || polygon.length < 3) return false;
      return isPointInPolygon(
        { latitude: lat, longitude: lng },
        polygon.map((p) => ({ latitude: p.lat, longitude: p.lng })),
      );
    },
    [],
  );

  // Вычислить центроид полигона
  const getCentroid = useCallback((polygon: PolygonPoint[]): [number, number] => {
    if (!polygon || polygon.length === 0) return [defaultLat, defaultLng];
    const lat = polygon.reduce((s, p) => s + p.lat, 0) / polygon.length;
    const lng = polygon.reduce((s, p) => s + p.lng, 0) / polygon.length;
    return [lat, lng];
  }, [defaultLat, defaultLng]);

  // Вычислить bounds полигона
  const getPolygonBounds = useCallback((polygon: PolygonPoint[]): L.LatLngBounds | null => {
    if (!polygon || polygon.length === 0) return null;
    const latlngs = polygon.map((p) => L.latLng(p.lat, p.lng));
    return L.latLngBounds(latlngs);
  }, []);

  // Cleanup при размонтировании
  useEffect(() => {
    const containerRef = mapRef.current;
    return () => {
      if (mapInstanceRef.current) {
        try {
          mapInstanceRef.current.off();
          mapInstanceRef.current.remove();
        } catch (e) {
          /* ignore */
        }
        mapInstanceRef.current = null;
      }
      if (containerRef) {
        const container = containerRef as any;
        if (container._leaflet_id) {
          delete container._leaflet_id;
        }
        container.innerHTML = '';
      }
      drawnItemsRef.current = null;
      drawControlRef.current = null;
      currentLayerRef.current = null;
      testMarkersRef.current = [];
    };
  }, []);

  // Синхронизация с данными формы
  useEffect(() => {
    const recordId = JSON.stringify(polygonInput.field.value);
    const recordChanged = currentRecordId !== null && recordId !== currentRecordId;

    if (!isInitialized || recordChanged) {
      const rawPolygon = polygonInput.field.value;
      let polygon: PolygonPoint[] = [];

      if (Array.isArray(rawPolygon) && rawPolygon.length >= 3) {
        polygon = rawPolygon.map((p: any) => ({
          lat: typeof p.lat === 'string' ? parseFloat(p.lat) : p.lat,
          lng: typeof p.lng === 'string' ? parseFloat(p.lng) : p.lng,
        }));
      }

      const color = colorInput.field.value || '#3388ff';

      setLocalPolygon(polygon);
      setLocalColor(color);
      setCurrentRecordId(recordId);
      setIsInitialized(true);
    }
  }, [isInitialized, polygonInput.field, colorInput.field, currentRecordId]);

  // Инициализация карты
  useEffect(() => {
    if (!mapRef.current || !isInitialized) return;

    // Удаляем старую карту
    if (mapInstanceRef.current) {
      try {
        mapInstanceRef.current.off();
        mapInstanceRef.current.remove();
      } catch (e) {
        /* ignore */
      }
      mapInstanceRef.current = null;
      drawnItemsRef.current = null;
      drawControlRef.current = null;
      currentLayerRef.current = null;
    }

    const timeoutId = setTimeout(() => {
      if (!mapRef.current) return;

      const container = mapRef.current as any;
      if (container._leaflet_id) {
        delete container._leaflet_id;
        container.innerHTML = '';
      }

      // Определяем начальный вид карты
      let initialCenter: [number, number] = [defaultLat, defaultLng];
      let initialZoom = 12;

      if (localPolygon.length >= 3) {
        const bounds = getPolygonBounds(localPolygon);
        if (bounds) {
          initialCenter = [bounds.getCenter().lat, bounds.getCenter().lng];
        }
      }

      // Создаём карту
      try {
        mapInstanceRef.current = L.map(mapRef.current, {
          zoomControl: true,
          attributionControl: true,
        }).setView(initialCenter, initialZoom);
      } catch (error) {
        if (container._leaflet_id) {
          delete container._leaflet_id;
        }
        container.innerHTML = '';
        mapInstanceRef.current = L.map(mapRef.current, {
          zoomControl: true,
          attributionControl: true,
        }).setView(initialCenter, initialZoom);
      }

      if (!mapInstanceRef.current) return;

      // Тайл-слой
      const tileUrl =
        mapType === 'satellite'
          ? 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
          : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';

      const attribution =
        mapType === 'satellite'
          ? 'Tiles &copy; Esri'
          : '&copy; OpenStreetMap contributors';

      currentLayerRef.current = L.tileLayer(tileUrl, {
        attribution,
        maxZoom: 18,
      }).addTo(mapInstanceRef.current);

      // Группа нарисованных слоев
      drawnItemsRef.current = new L.FeatureGroup();
      mapInstanceRef.current.addLayer(drawnItemsRef.current);

      // Если есть существующий полигон — нарисовать его
      if (localPolygon.length >= 3) {
        const latlngs = localPolygon.map((p) => L.latLng(p.lat, p.lng));
        const existingPolygon = L.polygon(latlngs, {
          color: localColor,
          fillColor: localColor,
          fillOpacity: 0.3,
          weight: 2,
        });
        drawnItemsRef.current.addLayer(existingPolygon);

        // Подогнать вид под полигон
        const bounds = getPolygonBounds(localPolygon);
        if (bounds) {
          mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50] });
        }
      }

      // Настраиваем Leaflet Draw
      // Русификация
      const drawLocal = (L as any).drawLocal;
      drawLocal.draw.toolbar.buttons.polygon = 'Нарисовать зону доставки';
      drawLocal.draw.toolbar.actions.title = 'Отменить';
      drawLocal.draw.toolbar.actions.text = 'Отмена';
      drawLocal.draw.toolbar.finish.title = 'Завершить';
      drawLocal.draw.toolbar.finish.text = 'Готово';
      drawLocal.draw.toolbar.undo.title = 'Удалить последнюю точку';
      drawLocal.draw.toolbar.undo.text = 'Назад';
      drawLocal.draw.handlers.polygon.tooltip.start = 'Кликните, чтобы начать рисовать зону';
      drawLocal.draw.handlers.polygon.tooltip.cont = 'Продолжайте кликать для добавления точек';
      drawLocal.draw.handlers.polygon.tooltip.end = 'Кликните на первую точку, чтобы закрыть зону';
      drawLocal.edit.toolbar.actions.save.title = 'Сохранить изменения';
      drawLocal.edit.toolbar.actions.save.text = 'Сохранить';
      drawLocal.edit.toolbar.actions.cancel.title = 'Отменить изменения';
      drawLocal.edit.toolbar.actions.cancel.text = 'Отмена';
      drawLocal.edit.toolbar.actions.clearAll.title = 'Удалить все';
      drawLocal.edit.toolbar.actions.clearAll.text = 'Удалить все';
      drawLocal.edit.toolbar.buttons.edit = 'Редактировать зону';
      drawLocal.edit.toolbar.buttons.editDisabled = 'Нет зон для редактирования';
      drawLocal.edit.toolbar.buttons.remove = 'Удалить зону';
      drawLocal.edit.toolbar.buttons.removeDisabled = 'Нет зон для удаления';
      drawLocal.edit.handlers.edit.tooltip.text = 'Перетаскивайте вершины для изменения формы';
      drawLocal.edit.handlers.edit.tooltip.subtext = 'Нажмите «Отмена» для отмены';
      drawLocal.edit.handlers.remove.tooltip.text = 'Кликните на зону, чтобы удалить';

      drawControlRef.current = new L.Control.Draw({
        position: 'topright',
        draw: {
          polygon: {
            allowIntersection: false,
            showArea: true,
            shapeOptions: {
              color: localColor,
              fillColor: localColor,
              fillOpacity: 0.3,
              weight: 2,
            },
          } as any,
          polyline: false,
          rectangle: false,
          circle: false,
          circlemarker: false,
          marker: false,
        },
        edit: {
          featureGroup: drawnItemsRef.current,
          remove: true,
        },
      });

      mapInstanceRef.current.addControl(drawControlRef.current);

      // Обработчик: новый полигон нарисован
      mapInstanceRef.current.on(L.Draw.Event.CREATED, (e: any) => {
        const layer = e.layer as L.Polygon;

        // Удаляем предыдущий полигон (поддерживаем только 1 зону)
        if (drawnItemsRef.current) {
          drawnItemsRef.current.clearLayers();
        }

        layer.setStyle({
          color: localColor,
          fillColor: localColor,
          fillOpacity: 0.3,
          weight: 2,
        });

        if (drawnItemsRef.current) {
          drawnItemsRef.current.addLayer(layer);
        }

        const points = extractPolygonPoints(layer);
        updatePolygonInForm(points);
      });

      // Обработчик: полигон отредактирован
      mapInstanceRef.current.on(L.Draw.Event.EDITED, (e: any) => {
        const layers = e.layers as L.LayerGroup;
        layers.eachLayer((layer: any) => {
          if (layer instanceof L.Polygon) {
            const points = extractPolygonPoints(layer);
            updatePolygonInForm(points);
          }
        });
      });

      // Обработчик: полигон удалён
      mapInstanceRef.current.on(L.Draw.Event.DELETED, () => {
        updatePolygonInForm(null);
      });

      // Обработчик клика — проверяем попадание в зону
      mapInstanceRef.current.on('click', async (e: L.LeafletMouseEvent) => {
        if (!mapInstanceRef.current) return;

        const { lat, lng } = e.latlng;
        const currentPoly = polygonInput.field.value as PolygonPoint[] | undefined;

        if (!currentPoly || currentPoly.length < 3) {
          // Если нет полигона, показываем координаты
          L.popup()
            .setLatLng(e.latlng)
            .setContent(
              `<strong>📍 Координаты</strong><br>` +
              `${lat.toFixed(6)}, ${lng.toFixed(6)}<br>` +
              `<small>Нарисуйте полигон зоны доставки</small>`,
            )
            .openOn(mapInstanceRef.current);
          return;
        }

        const inZone = isInPolygon(lat, lng, currentPoly);

        // Reverse geocoding через Яндекс
        let address = '';
        try {
          const yandexApiKey = '4b9a5d65-0573-49d8-8e08-b74df0f1977f';
          const resp = await fetch(
            `https://geocode-maps.yandex.ru/1.x/?apikey=${yandexApiKey}&geocode=${lng},${lat}&format=json&results=1`,
          );
          if (resp.ok) {
            const data = await resp.json();
            const results = data.response?.GeoObjectCollection?.featureMember || [];
            if (results.length > 0) {
              address = results[0].GeoObject.metaDataProperty.GeocoderMetaData.text;
            }
          }
        } catch {
          /* ignore */
        }

        const popupContent =
          `<strong>📍 ${address || 'Точка на карте'}</strong><br>` +
          `Координаты: ${lat.toFixed(6)}, ${lng.toFixed(6)}<br>` +
          `${inZone ? '✅ В зоне доставки' : '❌ Вне зоны доставки'}`;

        L.popup().setLatLng(e.latlng).setContent(popupContent).openOn(mapInstanceRef.current);

        setTestResult(
          `🖱️ КЛИК НА КАРТЕ\n\n` +
          `📍 Адрес: ${address || 'определяется...'}\n` +
          `📌 Координаты: ${lat.toFixed(6)}, ${lng.toFixed(6)}\n\n` +
          `${inZone ? '✅ Точка ПОПАДАЕТ в зону доставки' : '❌ Точка НЕ ПОПАДАЕТ в зону доставки'}`,
        );
      });
    }, 200);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [
    isInitialized,
    currentRecordId,
    mapType,
    // Не добавляем localPolygon/localColor в зависимости — 
    // иначе карта пересоздаётся при каждом изменении
  ]);

  // Переключение типа карты
  useEffect(() => {
    if (!mapInstanceRef.current) return;

    if (currentLayerRef.current) {
      try {
        mapInstanceRef.current.removeLayer(currentLayerRef.current);
      } catch {
        /* ignore */
      }
    }

    const tileUrl =
      mapType === 'satellite'
        ? 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
        : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';

    currentLayerRef.current = L.tileLayer(tileUrl, {
      attribution: mapType === 'satellite' ? 'Tiles &copy; Esri' : '&copy; OpenStreetMap',
      maxZoom: 18,
    }).addTo(mapInstanceRef.current);
  }, [mapType]);

  // Обновление цвета существующего полигона
  useEffect(() => {
    if (drawnItemsRef.current) {
      drawnItemsRef.current.eachLayer((layer: any) => {
        if (layer instanceof L.Polygon) {
          layer.setStyle({
            color: localColor,
            fillColor: localColor,
          });
        }
      });
    }
  }, [localColor]);

  // Синхронизация цвета из формы
  useEffect(() => {
    if (isInitialized && colorInput.field.value && colorInput.field.value !== localColor) {
      setLocalColor(colorInput.field.value);
    }
  }, [colorInput.field.value, isInitialized, localColor]);

  const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = e.target.value;
    setLocalColor(newColor);
    colorInput.field.onChange(newColor);
  };

  // Вычисляем площадь полигона для отображения
  const getPolygonArea = useCallback((polygon: PolygonPoint[]): string => {
    if (polygon.length < 3) return '—';
    // Формула Шолеса (приблизительная для небольших полигонов)
    const R = 6371000;
    let area = 0;
    for (let i = 0; i < polygon.length; i++) {
      const j = (i + 1) % polygon.length;
      const lat1 = (polygon[i].lat * Math.PI) / 180;
      const lat2 = (polygon[j].lat * Math.PI) / 180;
      const dLng = ((polygon[j].lng - polygon[i].lng) * Math.PI) / 180;
      area += dLng * (2 + Math.sin(lat1) + Math.sin(lat2));
    }
    area = Math.abs((area * R * R) / 2);
    if (area > 1_000_000) {
      return `${(area / 1_000_000).toFixed(2)} км²`;
    }
    return `${Math.round(area)} м²`;
  }, []);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Настройка зоны доставки (полигон)
      </Typography>

      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              🗺️ Используйте инструмент рисования (кнопка справа вверху на карте) для создания зоны
            </Typography>
            <Typography variant="caption" color="primary">
              💡 Кликайте по карте, чтобы обвести район — последняя точка замкнёт полигон
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <button
              type="button"
              onClick={() => setMapType('map')}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: mapType === 'map' ? 'bold' : 'normal',
                backgroundColor: mapType === 'map' ? '#1976d2' : '#f5f5f5',
                color: mapType === 'map' ? 'white' : '#333',
              }}
            >
              🗺️ Схема
            </button>
            <button
              type="button"
              onClick={() => setMapType('satellite')}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: mapType === 'satellite' ? 'bold' : 'normal',
                backgroundColor: mapType === 'satellite' ? '#1976d2' : '#f5f5f5',
                color: mapType === 'satellite' ? 'white' : '#333',
              }}
            >
              🛰️ Спутник
            </button>
          </Box>
        </Box>

        <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={2} mb={2}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Вершин полигона: <strong>{localPolygon.length}</strong>
            </Typography>
            {localPolygon.length >= 3 && (
              <Typography variant="body2" color="text.secondary">
                Площадь: <strong>{getPolygonArea(localPolygon)}</strong>
              </Typography>
            )}
          </Box>
          <Box>
            {localPolygon.length >= 3 && (
              <Typography variant="body2" color="text.secondary">
                Центроид: {getCentroid(localPolygon)[0].toFixed(4)},{' '}
                {getCentroid(localPolygon)[1].toFixed(4)}
              </Typography>
            )}
          </Box>
          <TextField
            label="Цвет зоны"
            type="color"
            value={localColor}
            onChange={handleColorChange}
            size="small"
            InputProps={{
              style: { height: '40px' },
            }}
          />
        </Box>

        {/* Тестирование адреса */}
        <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#666' }}>
            🔍 Проверка адреса — кликните на карте или введите адрес:
          </Typography>
          <Box display="grid" gridTemplateColumns="2fr 1fr 1fr" gap={1} mb={2}>
            <TextField
              label="Адрес (например: Большевистская)"
              value={testAddress}
              onChange={(e) => setTestAddress(e.target.value)}
              size="small"
              placeholder="Введите улицу..."
            />
            <button
              type="button"
              onClick={async () => {
                if (!testAddress) {
                  alert('Введите адрес!');
                  return;
                }

                setTestResult('Проверяем...');

                // Удаляем предыдущие тестовые маркеры
                testMarkersRef.current.forEach((marker) => {
                  if (mapInstanceRef.current) {
                    mapInstanceRef.current.removeLayer(marker);
                  }
                });
                testMarkersRef.current = [];

                try {
                  const yandexApiKey = '4b9a5d65-0573-49d8-8e08-b74df0f1977f';
                  const resp = await fetch(
                    `https://geocode-maps.yandex.ru/1.x/?apikey=${yandexApiKey}&geocode=Новосибирск,${encodeURIComponent(testAddress)}&format=json&results=5`,
                  );

                  if (!resp.ok) throw new Error('Ошибка Яндекс API');

                  const data = await resp.json();
                  const results =
                    data.response?.GeoObjectCollection?.featureMember || [];

                  if (results.length === 0) {
                    setTestResult('❌ Адрес не найден');
                    return;
                  }

                  const currentPoly = polygonInput.field.value as PolygonPoint[] | undefined;
                  let resultText = `🔍 Найдено вариантов: ${results.length}\n\n`;

                  results.forEach((item: any, index: number) => {
                    const geo = item.GeoObject;
                    const pos = geo.Point.pos.split(' ');
                    const testLng = parseFloat(pos[0]);
                    const testLat = parseFloat(pos[1]);
                    const foundAddress = geo.metaDataProperty.GeocoderMetaData.text;
                    const inZone =
                      currentPoly && currentPoly.length >= 3
                        ? isInPolygon(testLat, testLng, currentPoly)
                        : false;

                    resultText += `${index + 1}. ${foundAddress}\n`;
                    resultText += `   📌 ${testLat.toFixed(6)}, ${testLng.toFixed(6)}\n`;
                    resultText += `   ${inZone ? '✅ ПОПАДАЕТ в зону' : '❌ НЕ ПОПАДАЕТ в зону'}\n\n`;

                    // Маркер на карте
                    if (mapInstanceRef.current) {
                      try {
                        const marker = L.marker([testLat, testLng], {
                          icon: L.divIcon({
                            className: 'custom-div-icon',
                            html: `<div style="background-color: ${inZone ? '#4CAF50' : '#f44336'}; color: white; width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: bold; border: 2px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.3);">${index + 1}</div>`,
                            iconSize: [30, 30],
                            iconAnchor: [15, 15],
                          }),
                        }).addTo(mapInstanceRef.current);

                        marker.bindPopup(
                          `<strong>${index + 1}. ${foundAddress}</strong><br>` +
                          `${inZone ? '✅ В зоне' : '❌ Вне зоны'}`,
                        );

                        testMarkersRef.current.push(marker);
                      } catch {
                        /* ignore */
                      }
                    }
                  });

                  setTestResult(resultText);
                } catch (error) {
                  setTestResult('❌ Ошибка при поиске адреса');
                }
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              Проверить адрес
            </button>
            <button
              type="button"
              onClick={() => {
                testMarkersRef.current.forEach((marker) => {
                  if (mapInstanceRef.current) {
                    mapInstanceRef.current.removeLayer(marker);
                  }
                });
                testMarkersRef.current = [];
                setTestResult('');
                setTestAddress('');
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#f44336',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              Очистить
            </button>
          </Box>
          {testResult && (
            <Box
              sx={{
                p: 2,
                bgcolor: testResult.includes('✅') ? '#e8f5e9' : '#fff3e0',
                borderRadius: 1,
                whiteSpace: 'pre-line',
                fontSize: '13px',
                fontFamily: 'monospace',
              }}
            >
              {testResult}
            </Box>
          )}
        </Box>
      </Paper>

      <Box sx={{ mt: 1, mb: 1, p: 1, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #90caf9' }}>
        <Typography variant="caption" sx={{ color: '#1565c0' }}>
          💡 <strong>Как создать зону:</strong> Нажмите кнопку 🔷 (многоугольник) в правом верхнем углу карты,
          затем кликайте по карте, обводя нужный район. Последний клик — на первую точку (или дабл-клик) для замыкания. 
          Для редактирования — нажмите ✏️, перетаскивайте вершины.
        </Typography>
      </Box>

      <div ref={mapRef} style={{ height: '500px', width: '100%', marginTop: '8px', borderRadius: '8px' }} />
    </Box>
  );
};
